import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface PanelProps {
    title: ReactNode
    description?: ReactNode
    icon?: LucideIcon
    actions?: ReactNode
    children: ReactNode
    className?: string
    contentClassName?: string
    headingId?: string
}

const Panel: FC<PanelProps> = ({
    title,
    description,
    icon: Icon,
    actions,
    children,
    className,
    contentClassName,
    headingId
}): ReactNode => {
    return (
        <section
            aria-labelledby={headingId}
            className={cn('flex min-w-0 flex-col rounded-2xl border border-border bg-card shadow-xs', className)}
        >
            <header className='flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5 sm:px-5'>
                <div className='flex min-w-0 items-center gap-3'>
                    {Icon && (
                        <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground'>
                            <Icon className='size-[18px]' />
                        </span>
                    )}

                    <div className='min-w-0'>
                        <h2 id={headingId} className='font-heading text-[15px] font-semibold text-foreground'>
                            {title}
                        </h2>

                        {description && (
                            <p className='truncate text-xs text-muted-foreground'>
                                {description}
                            </p>
                        )}
                    </div>
                </div>

                {actions && (
                    <div className='flex flex-wrap items-center gap-2'>
                        {actions}
                    </div>
                )}
            </header>

            <div className={cn('min-w-0 flex-1', contentClassName)}>
                {children}
            </div>
        </section>
    )
}

export default Panel
